import type {
  BalancingInterval,
  BalancingPriceCurrency,
  BalancingPriceSide,
  MonthlyBalancingInterval,
  MonthlyBalancingResponse,
  MonthlyDayPriceSummary,
  MonthlyPriceSource,
  MonthlyPriceSummaries,
  PriceRange,
} from "./contracts";

/** Canonical conversion selector for daily interval values. */
export function displayDailyBalancingPrice(
  row: BalancingInterval,
  source: "estimated" | "final",
  side: BalancingPriceSide,
  currency: BalancingPriceCurrency,
  fxRate: number
): number | null {
  const sourceValue =
    source === "estimated"
      ? side === "positive"
        ? row.estimatedPricePositiveSource
        : row.estimatedPriceNegativeSource
      : side === "positive"
        ? row.finalPricePositiveSource
        : row.finalPriceNegativeSource;
  const sourceCurrency =
    source === "estimated" ? row.estimatedPriceCurrency : row.finalPriceCurrency;
  const eurValue =
    source === "estimated"
      ? side === "positive"
        ? row.estimatedPricePositiveEur
        : row.estimatedPriceNegativeEur
      : side === "positive"
        ? row.finalPricePositiveEur
        : row.finalPriceNegativeEur;

  if (currency === "EUR") return eurValue;
  if (sourceValue !== null && sourceCurrency === "RON") return sourceValue;
  if (sourceValue !== null && sourceCurrency === "EUR" && fxRate > 0) {
    return sourceValue * fxRate;
  }
  if (eurValue !== null && fxRate > 0) return eurValue * fxRate;
  return null;
}

export function selectMonthlyBalancingPrice(
  interval: MonthlyBalancingInterval,
  source: MonthlyPriceSource,
  side: BalancingPriceSide,
  currency: BalancingPriceCurrency
): number | null {
  if (source === "applied") {
    return (
      selectMonthlyBalancingPrice(interval, "final", side, currency) ??
      selectMonthlyBalancingPrice(interval, "estimated", side, currency)
    );
  }
  if (source === "final") {
    if (side === "positive" && currency === "EUR") return interval.finalPricePositiveEur;
    if (side === "positive" && currency === "RON") return interval.finalPricePositiveRon;
    if (side === "negative" && currency === "EUR") return interval.finalPriceNegativeEur;
    return interval.finalPriceNegativeRon;
  }
  if (side === "positive" && currency === "EUR") return interval.estimatedPricePositiveEur;
  if (side === "positive" && currency === "RON") return interval.estimatedPricePositiveRon;
  if (side === "negative" && currency === "EUR") return interval.estimatedPriceNegativeEur;
  return interval.estimatedPriceNegativeRon;
}

/** Simple-average daily summaries used by the monthly matrix at every width. */
export function selectMonthlyPriceSummaries(
  data: MonthlyBalancingResponse,
  source: MonthlyPriceSource,
  side: BalancingPriceSide,
  currency: BalancingPriceCurrency
): MonthlyPriceSummaries {
  const intervalsByDay = new Map<string, MonthlyBalancingInterval[]>();
  for (const interval of data.intervals) {
    const intervals = intervalsByDay.get(interval.deliveryDate) ?? [];
    intervals.push(interval);
    intervalsByDay.set(interval.deliveryDate, intervals);
  }

  const byDate = new Map<string, MonthlyDayPriceSummary>();
  for (const day of data.days) {
    const intervals = intervalsByDay.get(day.deliveryDate) ?? [];
    const bmValues = intervals.map((interval) =>
      selectMonthlyBalancingPrice(interval, source, side, currency)
    );
    const damValues = intervals.map((interval) =>
      currency === "EUR" ? interval.daPriceEur : interval.daPriceRon
    );
    byDate.set(day.deliveryDate, {
      deliveryDate: day.deliveryDate,
      expectedIntervals: day.quarters,
      availableIntervals: intervals.length,
      bmAvg: average(bmValues),
      bmCount: finiteValueCount(bmValues),
      damAvg: average(damValues),
      damCount: finiteValueCount(damValues),
    });
  }

  const summaries = [...byDate.values()];
  return {
    byDate,
    bmRange: numericRange(summaries.map((summary) => summary.bmAvg)),
    damRange: numericRange(summaries.map((summary) => summary.damAvg)),
  };
}

function average(values: Array<number | null | undefined>): number | null {
  const clean = values.filter(
    (value): value is number =>
      value !== null && value !== undefined && Number.isFinite(value)
  );
  if (clean.length === 0) return null;
  return clean.reduce((sum, value) => sum + value, 0) / clean.length;
}

function finiteValueCount(values: Array<number | null | undefined>): number {
  return values.filter(
    (value) => value !== null && value !== undefined && Number.isFinite(value)
  ).length;
}

function numericRange(values: Array<number | null | undefined>): PriceRange {
  const clean = values.filter(
    (value): value is number =>
      value !== null && value !== undefined && Number.isFinite(value)
  );
  if (clean.length === 0) return { min: 0, max: 0 };
  return { min: Math.min(...clean), max: Math.max(...clean) };
}
